import React, { useEffect, useMemo, useState } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { getCaseCategories, getCaseTypes, matchNameToCode } from '@lds-efsp/shared-contracts';
import { FormSelect } from '../../setup/components/FormInput';
import { useEFiling } from '../contexts/EFilingContext';
import { useApi, API_BASE_URL } from '../../../api/useApi';

const API_BASE = API_BASE_URL;

interface RefOption {
  code: string;
  name: string;
  efmCode?: string;
}

interface InlineCaseTypeSelectorProps {
  brand: BrandConfig;
  /**
   * Why the selector is showing, which swaps the heading/body copy:
   * - `not-detected` (default): OCR couldn't read a specific case type.
   * - `no-lead-types`: a type was set, but it can't file a case-initiating
   *   document (e.g. a complaint) — steer to a different type (WOL-330).
   * - `doc-class-mismatch`: a lead doc is uploaded whose detected class the
   *   chosen case type doesn't accept — steer to a different type (WOL-335).
   *   This case is rendered as an accessible alert (role="alert").
   * - `no-doc-types`: a type is set but the court offers no filing document
   *   types for it — steer to a different type (WOL-345). Also an alert.
   */
  reason?: 'not-detected' | 'no-lead-types' | 'doc-class-mismatch' | 'no-doc-types';
  /**
   * OCR-detected lead document class (e.g. "Complaint"). When present, the picker
   * shows a non-committal hint steering the user toward the case type that matches
   * their filing — it NEVER auto-selects a type. Null/undefined = no hint.
   */
  detectedLeadDocClass?: string | null;
}

/**
 * Step-1 case-type picker (WOL-329), rendered INSIDE the "Case Category & Type"
 * card in the Upload step's normal top-to-bottom flow (Walter 2026-07-10
 * R9–R11 — it used to be a separate yellow "Set your case type to continue" box
 * ABOVE the upload controls, duplicating the card and breaking the linear
 * flow). OCR resolves the case category/type most of the time, which un-sticks
 * the document-type list; when it can't (or steered wrong — WOL-330/335/345),
 * these dropdowns let the user set it without leaving Step 1. Mirrors
 * CaseInfoTab's reference-data loading and writes to the same `caseInfo` slice,
 * so navigating to the About Your Case step later shows the same selection.
 */
const InlineCaseTypeSelector: React.FC<InlineCaseTypeSelectorProps> = ({ brand, reason = 'not-detected', detectedLeadDocClass = null }) => {
  const { wizardState, updateCaseInfo, prefetchedCategories } = useEFiling();
  const { caseInfo, orderInfo } = wizardState;
  const { county } = orderInfo;
  const { fetchWithAuth } = useApi();

  const [categories, setCategories] = useState<RefOption[]>([]);
  const [caseTypes, setCaseTypes] = useState<RefOption[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);

  /* eslint-disable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- async fetch effects: synchronous reset/loading before fetch, setState in callbacks (mirrors CaseInfoTab) */
  // Categories — seed from the context prefetch, revalidate in the background.
  useEffect(() => {
    if (!county) {
      setCategories([]);
      return;
    }
    const prefetched = prefetchedCategories?.[county];
    if (prefetched?.length) {
      setCategories(prefetched);
    } else {
      setLoadingCategories(true);
    }
    fetchWithAuth(`${API_BASE}/api/efiling/reference/categories?county=${encodeURIComponent(county)}`)
      .then(res => res.json())
      .then(json => setCategories(json.data || []))
      .catch(() => setCategories(getCaseCategories(county)))
      .finally(() => setLoadingCategories(false));
  }, [county, fetchWithAuth, prefetchedCategories]);

  // OCR deferred match: category name detected but code empty → resolve by name.
  // Uses the shared matcher (exact → fuzzy → token-subset) so a paraphrased OCR
  // name still resolves, matching the server resolver's behavior. This is the
  // fallback for when the server could not resolve the code itself — no policy
  // for the county, or the extract ran before a court was picked.
  //
  // SOR-173: it therefore reads the banner-only field too. That field holds the
  // extracted name in exactly the case this effect exists for (server left the
  // code empty), so sourcing only `caseCategoryName` would strand the fallback.
  // `caseCategoryName` is kept as a source defensively: hydration now routes a
  // legacy draft's code-less name across (`backfillRejectedCategoryName`) and
  // every other writer sets the pair together, so it should not be reachable
  // with an empty code — but a future writer that does would be stranded here
  // rather than silently unresolvable.
  const deferredCategoryMatch = useMemo(() => {
    const pendingName = caseInfo.caseCategoryName || caseInfo.ocrExtractedCategoryName;
    if (!pendingName || caseInfo.caseCategoryCode || categories.length === 0) return null;
    return matchNameToCode(
      pendingName,
      categories.map(c => ({ code: c.efmCode || c.code, name: c.name })),
    );
  }, [
    caseInfo.caseCategoryName,
    caseInfo.ocrExtractedCategoryName,
    caseInfo.caseCategoryCode,
    categories,
  ]);

  useEffect(() => {
    if (!deferredCategoryMatch) return;
    // A name that resolves IS the filing's category. When it came from the
    // banner-only field, `caseCategoryName` is empty — adopt the court's own
    // label for the matched code so the display surfaces have a value to show
    // and the doc-type fetch (keyed on that name) is never skipped.
    updateCaseInfo(
      caseInfo.caseCategoryName
        ? { caseCategoryCode: deferredCategoryMatch.code }
        : { caseCategoryCode: deferredCategoryMatch.code, caseCategoryName: deferredCategoryMatch.name },
    );
  }, [deferredCategoryMatch, caseInfo.caseCategoryName, updateCaseInfo]);

  // Case types for the selected category.
  useEffect(() => {
    if (!caseInfo.caseCategoryCode) {
      setCaseTypes([]);
      return;
    }
    setLoadingTypes(true);
    fetchWithAuth(`${API_BASE}/api/efiling/reference/types/${encodeURIComponent(caseInfo.caseCategoryCode)}?county=${encodeURIComponent(county)}`)
      .then(res => res.json())
      .then(json => setCaseTypes(json.data || []))
      .catch(() => setCaseTypes(getCaseTypes(caseInfo.caseCategoryCode)))
      .finally(() => setLoadingTypes(false));
  }, [caseInfo.caseCategoryCode, county, fetchWithAuth]);

  // OCR deferred match: type name detected but code empty → resolve by name.
  // Shared matcher tolerates paraphrase ("Contract / Warranty" → "Contract:
  // Breach Cont/Warranty (06)"); on a hit we also adopt the canonical policy
  // name so the rest of the wizard shows the court's exact label.
  //
  // SOR-194: it reads the banner-only field too, exactly as the category match
  // above does and for the same reason. This effect exists for the case where
  // the SERVER left the code empty — no policy for the county, or the extract
  // ran before a court was picked — and that is now precisely the case whose
  // name lives in `ocrExtractedCaseTypeName` rather than in `caseType`. Sourcing
  // only `caseType.name` would leave the fallback with nothing to match on, and
  // a name the server could not resolve for want of a court is exactly the one
  // this deferred pass CAN resolve once the court's types have loaded.
  // `caseType.name` is kept as a source defensively: hydration routes a legacy
  // draft's code-less name across (`backfillRejectedCaseTypeName`) and every
  // other writer sets the pair together, so it should not be reachable with an
  // empty code — but a future writer that does would be stranded here rather
  // than silently unresolvable.
  useEffect(() => {
    const pendingName = caseInfo.caseType?.name || caseInfo.ocrExtractedCaseTypeName;
    if (!pendingName || caseInfo.caseType?.code || caseTypes.length === 0) return;
    const match = matchNameToCode(pendingName, caseTypes);
    if (match) updateCaseInfo({ caseType: { code: match.code, name: match.name } });
  }, [
    caseInfo.caseType?.name,
    caseInfo.caseType?.code,
    caseInfo.ocrExtractedCaseTypeName,
    caseTypes,
    updateCaseInfo,
  ]);
  /* eslint-enable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */

  const categoryOptions = [
    { value: '', label: loadingCategories ? 'Loading...' : 'Select case category...' },
    ...categories.map(c => ({ value: c.efmCode || c.code, label: c.name })),
  ];
  const caseTypeOptions = [
    { value: '', label: loadingTypes ? 'Loading...' : 'Select case type...' },
    ...caseTypes.map(ct => ({ value: ct.code, label: ct.name })),
  ];

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    const match = categories.find(c => (c.efmCode || c.code) === code);
    // Reset the type whenever the category changes — its options just changed.
    updateCaseInfo({ caseCategoryCode: code, caseCategoryName: match?.name || '', caseType: null });
  };

  const handleCaseTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    const match = caseTypes.find(ct => ct.code === code);
    updateCaseInfo({ caseType: match ? { code: match.code, name: match.name } : null });
  };

  // REQ-OCR-4 (Madera doc-types incident): a detected category name that
  // matches NONE of this court's categories must say so — the silent
  // alternative was an empty Case Type dropdown plus a misleading
  // "no document types" banner. Takes precedence over the passed `reason`.
  //
  // SOR-173: the trigger is the DECLARED banner-only field rather than a
  // re-inference over `caseCategoryName`. `applyExtraction` already decided
  // "the court rejected this name" when it routed the value here, so the
  // component reads that decision instead of re-deriving it — and, because it
  // no longer depends on the categories fetch having settled, the banner and
  // its candidate chips render even when that fetch fails. The one thing still
  // worth waiting for is the deferred match above, which can yet resolve the
  // name client-side; excluding it keeps a resolvable name from flashing the
  // banner for one paint.
  const categoryNotRecognized =
    !!caseInfo.ocrExtractedCategoryName &&
    !caseInfo.caseCategoryCode &&
    !deferredCategoryMatch;

  // REQ-OCR-2: parent-category candidates carried from the extraction (the
  // detected value is a case TYPE under several categories). Offered as
  // one-click choices; picking one behaves exactly like the dropdown.
  //
  // SOR-173: rendered as carried — exactly the resolution's candidate set, no
  // more and no fewer. The old `categories.some(...)` filter cross-checked each
  // candidate against the client's own categories fetch and silently dropped
  // any it hadn't loaded, so a slow or failed fetch collapsed the recovery to a
  // banner with nothing under it. The resolver derives these codes from the
  // court's own policy; a second opinion here can only subtract.
  const categoryCandidates = categoryNotRecognized
    ? (caseInfo.caseCategoryCandidates ?? [])
    : [];

  const handlePickCandidate = (cand: { code: string; name: string }) => {
    // Keep the type when the extraction resolved one — the candidate parents
    // all admit it by construction (they were derived from its relationships).
    updateCaseInfo({ caseCategoryCode: cand.code, caseCategoryName: cand.name });
  };

  const isAlertReason =
    categoryNotRecognized || reason === 'doc-class-mismatch' || reason === 'no-doc-types';

  return (
    <div
      id="inline-case-type"
      data-testid="inline-case-type-selector"
      role={isAlertReason ? 'alert' : undefined}
      aria-live={isAlertReason ? 'assertive' : undefined}
    >
      {categoryNotRecognized ? (
        <>
          <p
            data-testid="category-not-recognized"
            className="text-sm font-semibold mb-1"
            style={{ color: brand.colors.warning || '#B45309' }}
          >
            This court files “{caseInfo.ocrExtractedCategoryName}” under a different category
          </p>
          <p className="text-xs mb-3" style={{ color: brand.colors.textLight }}>
            {categoryCandidates.length > 0
              ? 'We read the case category from your document, but this court organizes it differently. Pick the matching category below and the rest fills in.'
              : "We read a case category from your document, but it isn't one of the categories this court accepts. Pick the closest category below and the document-type list will load."}
          </p>
          {categoryCandidates.length > 0 && (
            <div data-testid="category-candidates" className="flex flex-wrap gap-2 mb-3">
              {categoryCandidates.map(cand => (
                <button
                  key={cand.code}
                  type="button"
                  data-testid={`category-candidate-${cand.code}`}
                  onClick={() => handlePickCandidate(cand)}
                  className="px-3 py-1.5 rounded-full border text-xs font-medium transition-colors"
                  style={{
                    borderColor: brand.colors.primary,
                    color: brand.colors.link,
                    backgroundColor: 'transparent',
                  }}
                >
                  {cand.name}
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          {reason !== 'not-detected' && (
            <p className="text-sm font-semibold mb-1" style={{ color: brand.colors.warning || '#B45309' }}>
              {reason === 'no-lead-types'
                ? "This case type can't start a new case"
                : reason === 'doc-class-mismatch'
                  ? "This case type doesn't accept your document"
                  : 'No filing document types available for this case type'}
            </p>
          )}
          <p className="text-xs mb-3" style={{ color: brand.colors.textLight }}>
            {reason === 'no-lead-types'
              ? "The case type you picked has no case-initiating documents (like a Complaint or Petition), so you can't file it to open a case. Choose a different case type and the document-type list will load."
              : reason === 'doc-class-mismatch'
                ? "The case type you selected doesn't accept the document you uploaded, so it isn't in the document-type list. Choose a different case type and the list will update."
                : reason === 'no-doc-types'
                  ? 'This court does not offer any filing document types for the selected case type. If this is unexpected, choose a different case type, or contact support.'
                  : caseInfo.caseType?.code
                    ? 'Filing under the case category and type below — change them here if needed.'
                    : "We couldn't read the case type from your document. Pick it here to load the document-type list — no need to leave this step."}
          </p>
        </>
      )}
      {detectedLeadDocClass && (
        <p
          data-testid="casetype-suggestion-hint"
          className="text-xs mb-3 font-medium"
          style={{ color: brand.colors.text }}
        >
          📄 We detected a <strong>{detectedLeadDocClass}</strong> — pick the case
          type that matches your claim to start a new case.
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormSelect
          brand={brand}
          label="Case Category"
          name="inlineCaseCategory"
          value={caseInfo.caseCategoryCode}
          onChange={handleCategoryChange}
          options={categoryOptions}
        />
        <FormSelect
          brand={brand}
          label="Case Type"
          name="inlineCaseType"
          value={caseInfo.caseType?.code || ''}
          onChange={handleCaseTypeChange}
          options={caseTypeOptions}
          disabled={!caseInfo.caseCategoryCode}
        />
      </div>
    </div>
  );
};

export default InlineCaseTypeSelector;
